import { useTenantAvailable } from '@api/application-configuration';
import { useQueryClient } from '@tanstack/react-query';
import { NOTIFICATIONS_LIST_QUERY_KEY } from '@unistocks/features/notifications/utils';
import { selectSelectedPortfolioId } from '@unistocks/features/portfolios/store/portfolio.slice';
import { selectSelectedSavingsGoalId } from '@unistocks/features/savings-goals/store/savings-goals.slice';
import { selectSelectedWatchlistId } from '@unistocks/features/watchlists/store/watchlist.slice';
import {
  externalAccountsGetAccounts,
  externalAccountsGetProviders
} from '@unistocks/proxy/axios-client/ExternalAccountsClient';
import {
  notificationsGetList
} from '@unistocks/proxy/axios-client/NotificationsClient';
import {
  portfoliosGet,
  portfoliosGetList
} from '@unistocks/proxy/axios-client/PortfoliosClient';
import {
  profileGet
} from '@unistocks/proxy/axios-client/ProfileClient';
import {
  savingsGoalsGet,
  savingsGoalsGetList
} from '@unistocks/proxy/axios-client/SavingsGoalsClient';
import {
  watchlistsGet,
  watchlistsGetList
} from '@unistocks/proxy/axios-client/WatchlistsClient';
import {
  externalAccountsGetAccountsQueryKey,
  externalAccountsGetProvidersQueryKey,
  getExternalAccountsGetAccountsDefaultOptions,
  getExternalAccountsGetProvidersDefaultOptions
} from '@unistocks/proxy/axios-client/ExternalAccountsQuery';
import {
  getPortfoliosGetDefaultOptions,
  getPortfoliosGetListDefaultOptions,
  portfoliosGetListQueryKey,
  portfoliosGetQueryKey
} from '@unistocks/proxy/axios-client/PortfoliosQuery';
import {
  getSavingsGoalsGetDefaultOptions,
  savingsGoalsGetListQueryKey,
  savingsGoalsGetQueryKey
} from '@unistocks/proxy/axios-client/SavingsGoalsQuery';
import {
  getWatchlistsGetDefaultOptions,
  getWatchlistsGetListDefaultOptions,
  watchlistsGetListQueryKey,
  watchlistsGetQueryKey
} from '@unistocks/proxy/axios-client/WatchlistsQuery';
import {
  profileGetQueryKey
} from '@unistocks/proxy/axios-client/ProfileQuery';
import { MAX_RESULT_COUNT } from '@unistocks/shared/consts';
import { useAppSelector } from '@unistocks/shared/hooks/redux-hooks';
import { Logger } from '@unistocks/shared/services/logger.service';
import { Image } from 'expo-image';
import { useEffect } from 'react';

export const usePrefetchUserQueries = () => {
  const queryClient = useQueryClient();
  const isTenantAvailable = useTenantAvailable();
  const selectedPortfolioId = useAppSelector(selectSelectedPortfolioId);
  const selectedWatchlistId = useAppSelector(selectSelectedWatchlistId);
  const selectedSavingsGoalId = useAppSelector(selectSelectedSavingsGoalId);

  useEffect(() => {
    const prefetch = async () => {
      try {
        // Prefetch portfolio list
        queryClient.prefetchQuery({
          queryKey: portfoliosGetListQueryKey(),
          queryFn: () => portfoliosGetList(),
          ...getPortfoliosGetListDefaultOptions()
        });

        // Prefetch selected portfolio
        if (selectedPortfolioId) {
          queryClient.prefetchQuery({
            queryKey: portfoliosGetQueryKey(selectedPortfolioId),
            queryFn: () => portfoliosGet(selectedPortfolioId),
            ...getPortfoliosGetDefaultOptions()
          });
        }

        // Prefetch watchlist list
        queryClient.prefetchQuery({
          queryKey: watchlistsGetListQueryKey(),
          queryFn: () => watchlistsGetList(),
          ...getWatchlistsGetListDefaultOptions()
        });

        // Prefetch selected watchlist
        if (selectedWatchlistId) {
          queryClient.prefetchQuery({
            queryKey: watchlistsGetQueryKey(selectedWatchlistId),
            queryFn: () => watchlistsGet(selectedWatchlistId),
            ...getWatchlistsGetDefaultOptions()
          });
        }

        // Prefetch external accounts and providers
        queryClient.prefetchQuery({
          queryKey: externalAccountsGetAccountsQueryKey(),
          queryFn: () => externalAccountsGetAccounts(),
          ...getExternalAccountsGetAccountsDefaultOptions()
        });

        queryClient.prefetchQuery({
          queryKey: externalAccountsGetProvidersQueryKey('SE'),
          queryFn: () => externalAccountsGetProviders('SE'),
          ...getExternalAccountsGetProvidersDefaultOptions()
        });

        queryClient.prefetchInfiniteQuery({
          queryKey: NOTIFICATIONS_LIST_QUERY_KEY,
          queryFn: ({ pageParam, signal }) =>
            notificationsGetList(pageParam.skipCount, pageParam.maxResultCount, { signal }),
          initialPageParam: { skipCount: 0, maxResultCount: MAX_RESULT_COUNT }
        });

        // Prefetch savings goals list
        queryClient.prefetchQuery({
          queryKey: savingsGoalsGetListQueryKey(),
          queryFn: () => savingsGoalsGetList()
        });

        // Prefetch selected savings goal
        if (selectedSavingsGoalId) {
          queryClient.prefetchQuery({
            queryKey: savingsGoalsGetQueryKey(selectedSavingsGoalId),
            queryFn: () => savingsGoalsGet(selectedSavingsGoalId),
            ...getSavingsGoalsGetDefaultOptions()
          });
        }

        // Prefetch user profile
        queryClient.prefetchQuery({
          queryKey: profileGetQueryKey(),
          queryFn: async () => {
            const profile = await profileGet();

            if (profile.extraProperties.ProfilePictureUrl)
              await Image.prefetch(profile.extraProperties.ProfilePictureUrl);

            return profile;
          }
        });
      } catch (error) {
        Logger.error(error, 'Failed to prefetch user queries');
      }
    };

    if (isTenantAvailable) prefetch();
  }, [
    isTenantAvailable,
    queryClient,
    selectedPortfolioId,
    selectedSavingsGoalId,
    selectedWatchlistId
  ]);
};
